/**
 * TECH-EXTRAS (Seth 2026-09-29, mobile review):
 *  - YesterdayCard — a facts-only recap on Today: calls closed, plans sold,
 *    invoiced (once the mirror has it), shoutouts. No fake coaching — goals
 *    and per-tech coaching are PARKED in BACKLOG §N until Seth defines what
 *    a good day means per role; a recap of true numbers is useful today.
 *  - ScorePane — the shop-TV scoreboard, restyled glass for the phone, in
 *    the More menu (Today stays operational; competition is a glance you
 *    choose). Same data as /team/tv: useScoreboard week|month.
 * Both ride the LIQUID-GLASS tilt where it feels right.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Trophy, Star, X, CalendarCheck2 } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/cn";
import { useScoreboard, type Period } from "@/features/team/scoreboard";
import { useTouchTilt } from "./tilt";

const isoDay = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const money0 = (n: number) => `$${Math.round(n).toLocaleString("en-US")}`;
const doneStatus = (s: string | null) => {
  const t = (s ?? "").toLowerCase();
  return t.startsWith("comp") || t === "done";
};

export interface YesterdayRecap {
  day: string;
  calls: number;
  invoiced: number;
  plans: number;
  shoutouts: number;
}

export function useYesterdayRecap(empno: string | null) {
  return useQuery({
    queryKey: ["m_yesterday", empno],
    enabled: !!empno,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<YesterdayRecap> => {
      const y = new Date();
      y.setDate(y.getDate() - 1);
      const day = isoDay(y);
      const [disp, sales, esc, peg, shout] = await Promise.all([
        supabase.from("v_dispatch_full").select("status").eq("appointment_date", day).eq("tech_empno", empno as string).limit(60),
        supabase.from("mirror_sales").select("inv_amount").eq("inv_date", day).eq("salesman", empno as string).limit(300),
        supabase.from("mirror_serv_agr").select("salesman").eq("beg_date", day).eq("salesman", empno as string).limit(60),
        supabase.from("pegasus_plan_sale").select("status").eq("sold_by", empno as string).gte("sold_at", `${day}T00:00:00`).lte("sold_at", `${day}T23:59:59`).limit(60),
        supabase.from("pegasus_review_shoutout").select("id").eq("review_date", day).eq("empno", empno as string).limit(30),
      ]);
      return {
        day,
        calls: (disp.data ?? []).filter((d) => doneStatus(d.status as string | null)).length,
        invoiced: (sales.data ?? []).reduce((s, r) => s + Number((r as { inv_amount: number | null }).inv_amount ?? 0), 0),
        plans:
          (esc.data?.length ?? 0) +
          ((peg.data ?? []) as { status: string | null }[]).filter((p) => p.status !== "cancelled").length,
        shoutouts: shout.data?.length ?? 0,
      };
    },
  });
}

const YDAY_DISMISS_KEY = "pegasus.m.yday.dismissed";

/** Today tab: yesterday's true numbers, dismissible for the day. */
export function YesterdayCard({ empno }: { empno: string | null }) {
  const { data } = useYesterdayRecap(empno);
  const [, bump] = useState(0);
  const tilt = useTouchTilt(4);
  if (!data) return null;
  const any = data.calls > 0 || data.plans > 0 || data.shoutouts > 0 || data.invoiced > 0;
  if (!any) return null;
  let dismissed = false;
  try {
    dismissed = localStorage.getItem(YDAY_DISMISS_KEY) === data.day;
  } catch {
    // fine
  }
  if (dismissed) return null;
  const dayLabel = new Date(`${data.day}T12:00:00`).toLocaleDateString("en-US", { weekday: "long" });
  const Pill = ({ big, label }: { big: string; label: string }) => (
    <div className="min-w-0 flex-1 rounded-2xl bg-[var(--m-soft,#eef0f3)] px-3 py-2.5 text-center">
      <div className="text-[20px] font-extrabold tabular-nums tracking-tight text-[var(--m-ink,#0f172a)]">{big}</div>
      <div className="truncate text-[11.5px] font-semibold text-[var(--m-ink3,#64748b)]">{label}</div>
    </div>
  );
  return (
    <section ref={tilt.ref} {...tilt.handlers} className="m-card m-tilt mt-3 overflow-hidden p-4">
      <div className="flex items-center gap-2">
        <CalendarCheck2 className="h-[18px] w-[18px] text-[var(--m-ink3,#64748b)]" />
        <span className="m-eyebrow">Yesterday · {dayLabel}</span>
        <button
          type="button"
          aria-label="Dismiss"
          onClick={() => {
            try {
              localStorage.setItem(YDAY_DISMISS_KEY, data.day);
            } catch {
              // fine
            }
            bump((n) => n + 1);
          }}
          className="ml-auto grid h-8 w-8 place-items-center rounded-full text-[var(--m-ink3,#94a3b8)] [touch-action:manipulation] active:scale-95"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="mt-2.5 flex gap-2">
        <Pill big={String(data.calls)} label={data.calls === 1 ? "Call Closed" : "Calls Closed"} />
        {data.plans > 0 && <Pill big={String(data.plans)} label={data.plans === 1 ? "Plan Sold" : "Plans Sold"} />}
        {data.invoiced > 0 && <Pill big={money0(data.invoiced)} label="Invoiced" />}
        {data.shoutouts > 0 && <Pill big={`★${data.shoutouts}`} label={data.shoutouts === 1 ? "Shoutout" : "Shoutouts"} />}
      </div>
    </section>
  );
}

/** More → Scoreboard: the shop TV's numbers, phone-glass. */
export function ScorePane({ empno, onClose }: { empno: string | null; onClose: () => void }) {
  const [period, setPeriod] = useState<Period>("week");
  const { data, isLoading } = useScoreboard(period);
  const rows = data?.rows ?? [];
  const myRank = empno ? rows.findIndex((r) => r.empno === empno) : -1;
  const rankTone = (i: number) =>
    i === 0 ? "text-amber-500" : i === 1 ? "text-slate-400" : i === 2 ? "text-amber-700" : "text-[var(--m-ink3,#94a3b8)]";

  return (
    <div className="fixed inset-0 z-[45] mx-auto flex max-w-md flex-col bg-[#f6f5f1]" data-tech-app>
      <header className="border-b border-slate-200 bg-white px-3 pb-2.5 pt-[max(env(safe-area-inset-top),10px)]">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-600 [touch-action:manipulation] active:scale-95"
            aria-label="Back"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="min-w-0">
            <div className="text-[17px] font-bold text-slate-900">Scoreboard</div>
            <div className="text-[12px] text-slate-500">
              {myRank >= 0 ? `You're #${myRank + 1} this ${period}` : `Same board as the shop TV`}
            </div>
          </div>
          <div className="m-seg ml-auto" role="radiogroup" aria-label="Period">
            {(["week", "month"] as const).map((p) => (
              <button key={p} type="button" role="radio" aria-checked={period === p} onClick={() => setPeriod(p)} className={cn(period === p && "on")}>
                {p === "week" ? "Week" : "Month"}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="min-h-0 flex-1 overflow-y-auto px-4 pb-24 pt-4">
        {isLoading ? (
          <div className="space-y-3">
            <div className="h-24 animate-pulse rounded-3xl bg-slate-200/70" />
            <div className="h-64 animate-pulse rounded-3xl bg-slate-200/60" />
          </div>
        ) : rows.length === 0 ? (
          <div className="m-card p-6 text-center text-[14px] text-[var(--m-ink3,#64748b)]">
            Nothing on the board yet this {period}.
          </div>
        ) : (
          <>
            <div className="m-card overflow-hidden p-2">
              {rows.map((r, i) => {
                const me = r.empno === empno;
                return (
                  <div
                    key={r.empno}
                    className={cn(
                      "flex items-center gap-3 rounded-2xl px-3 py-2.5",
                      me && "bg-[var(--m-tint,rgba(15,23,42,0.06))]",
                    )}
                  >
                    <span className={cn("w-7 text-center text-[16px] font-extrabold tabular-nums", rankTone(i))}>
                      {i < 3 ? <Trophy className="mx-auto h-[18px] w-[18px]" /> : i + 1}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1.5">
                        <span className="truncate text-[15px] font-bold text-[var(--m-ink,#0f172a)]">
                          {r.name.split(/\s+/)[0]}
                        </span>
                        {me && <span className="m-chip m-chip-tint">You</span>}
                      </span>
                      <span className="block truncate text-[12px] text-[var(--m-ink3,#64748b)]">
                        {[
                          r.plans > 0 ? `${r.plans} ${r.plans === 1 ? "plan" : "plans"}` : null,
                          r.invoiced > 0 ? money0(r.invoiced) : null,
                          r.shoutouts > 0 ? `★${r.shoutouts}` : null,
                        ]
                          .filter(Boolean)
                          .join(" · ") || "—"}
                      </span>
                    </span>
                    <span className="text-right">
                      <span className="block text-[18px] font-extrabold tabular-nums text-[var(--m-ink,#0f172a)]">{r.calls}</span>
                      <span className="block text-[10.5px] font-semibold uppercase tracking-wide text-[var(--m-ink3,#94a3b8)]">calls</span>
                    </span>
                  </div>
                );
              })}
            </div>
            {data?.invoicedAsOf && (
              <p className="mt-2 px-1 text-[11.5px] text-[var(--m-ink3,#94a3b8)]">
                Invoiced totals as synced through {new Date(`${data.invoicedAsOf}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" })}.
              </p>
            )}
            {(data?.shoutoutList?.length ?? 0) > 0 && (
              <div className="m-card mt-3 p-4">
                <div className="flex items-center gap-2">
                  <Star className="h-[18px] w-[18px] text-amber-500" />
                  <span className="m-eyebrow">Customer Shoutouts</span>
                </div>
                <div className="mt-2 space-y-2.5">
                  {(data?.shoutoutList ?? []).slice(0, 3).map((s) => (
                    <div key={s.id} className="rounded-2xl bg-[var(--m-soft,#eef0f3)] px-3.5 py-2.5">
                      {s.quote && <p className="line-clamp-2 text-[13.5px] leading-snug text-[var(--m-ink,#0f172a)]">"{s.quote}"</p>}
                      <p className="mt-1 text-[11.5px] font-semibold text-[var(--m-ink3,#64748b)]">
                        {"★".repeat(Math.max(0, Math.min(5, s.stars ?? 5)))}
                        {s.reviewer_name ? ` · ${s.reviewer_name}` : ""}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

/* ------------------------------------------------------------ OFFLINE-01 */
// The header truth chip + the Today note when the day is served from the
// snapshot. Facts, quietly: amber means no signal; the count means queued
// changes that will send themselves.
import { WifiOff, CloudUpload } from "lucide-react";
import { useOutbox } from "@/lib/outbox";

export function OfflineChip() {
  const { pending, offline } = useOutbox();
  if (!offline && pending === 0) return null;
  return (
    <span
      className={cn(
        "m-chip",
        offline ? "m-chip-warn" : "m-chip-tint",
      )}
      title={offline ? "No signal — changes are saved on this device and will send when you're back." : "Sending saved changes…"}
    >
      {offline ? <WifiOff className="h-3.5 w-3.5" /> : <CloudUpload className="h-3.5 w-3.5" />}
      {offline ? (pending > 0 ? `Offline · ${pending}` : "Offline") : `Sending ${pending}`}
    </span>
  );
}

export function OfflineDayNote() {
  return (
    <div className="mb-3 flex items-center gap-2.5 rounded-2xl bg-amber-50 px-3.5 py-2.5 ring-1 ring-amber-200/70">
      <WifiOff className="h-4 w-4 shrink-0 text-amber-600" />
      <p className="text-[13px] font-semibold leading-snug text-amber-800">
        No signal — showing your last loaded day. Status taps still work and send themselves later.
      </p>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
 * MyProposalsPane (QUOTE-PKG-01, Seth 2026-09-30: "for them to be able to
 * view their proposals after") — every quote THIS tech prepared, newest
 * first: status chip (live — viewed/accepted update on their own), customer,
 * title, headline total. Tap opens the proposal exactly as the homeowner
 * sees it (/proposal?t=…). Reads pegasus_quote by prepared_by_empno +
 * v_quote_list for the totals; names from the customer mirror.
 * ──────────────────────────────────────────────────────────────────────── */
import { FileText as FileTextIcon } from "lucide-react";
import { displayStatus as quoteDisplayStatus } from "@/features/quotes/quotes";

interface MyQuoteRow {
  id: string;
  title: string | null;
  status: string;
  quote_type: string;
  custno: string | null;
  token: string | null;
  created_at: string;
  sent_at: string | null;
  viewed_at: string | null;
  expires_at: string | null;
  accepted_at: string | null;
  customer: string;
  total: number | null;
}

function useMyProposals(empno: string | null) {
  return useQuery({
    queryKey: ["my_proposals", empno],
    enabled: !!empno,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
    queryFn: async (): Promise<MyQuoteRow[]> => {
      const { data, error } = await supabase
        .from("pegasus_quote")
        .select("id, title, status, quote_type, custno, token, created_at, sent_at, viewed_at, expires_at, accepted_at")
        .eq("prepared_by_empno", empno!)
        .eq("is_template", false)
        .eq("is_practice", false)
        .order("created_at", { ascending: false })
        .limit(60);
      if (error) throw error;
      const rows = (data ?? []) as Omit<MyQuoteRow, "customer" | "total">[];
      const ids = rows.map((r) => r.id);
      const custnos = [...new Set(rows.map((r) => r.custno).filter(Boolean))] as string[];
      const [names, totals] = await Promise.all([
        custnos.length
          ? supabase.from("mirror_customer").select("custno, fullname").in("custno", custnos)
          : Promise.resolve({ data: [] as never[] }),
        ids.length
          ? supabase.from("v_quote_list").select("id, headline_total").in("id", ids)
          : Promise.resolve({ data: [] as never[] }),
      ]);
      const nameMap = new Map(((names.data ?? []) as { custno: string; fullname: string | null }[]).map((c) => [c.custno, c.fullname ?? c.custno]));
      const totalMap = new Map(((totals.data ?? []) as { id: string; headline_total: number | null }[]).map((t) => [t.id, t.headline_total]));
      return rows.map((r) => ({
        ...r,
        customer: (r.custno ? nameMap.get(r.custno) ?? r.custno : "Customer").replace(/\s*\d{6,7}\s*$/, ""),
        total: totalMap.get(r.id) ?? null,
      }));
    },
  });
}

const Q_CHIP: Record<string, string> = {
  draft: "bg-slate-100 text-slate-500",
  sent: "bg-sky-100 text-sky-700",
  viewed: "bg-violet-100 text-violet-700",
  accepted: "bg-emerald-100 text-emerald-700",
  declined: "bg-rose-100 text-rose-700",
  expired: "bg-amber-100 text-amber-700",
};

export function MyProposalsPane({ empno, onClose }: { empno: string | null; onClose: () => void }) {
  const { data, isLoading } = useMyProposals(empno);
  const rows = data ?? [];
  const fmtDay = (iso: string) =>
    new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const money0 = (v: number) => "$" + Math.round(v).toLocaleString("en-US");

  return (
    <div className="fixed inset-0 z-[45] mx-auto flex max-w-md flex-col bg-[#f6f5f1]" data-tech-app>
      <header className="border-b border-slate-200 bg-white px-3 pb-2.5 pt-[max(env(safe-area-inset-top),10px)]">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-600 [touch-action:manipulation] active:scale-95"
            aria-label="Back"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="min-w-0 flex-1">
            <div className="text-base font-bold text-slate-900">My Proposals</div>
            <div className="text-[11px] text-slate-500">everything you've presented — statuses update on their own</div>
          </div>
        </div>
      </header>
      <div className="min-h-0 flex-1 space-y-2.5 overflow-y-auto p-3 pb-10">
        {isLoading ? (
          <div className="h-24 animate-pulse rounded-3xl bg-slate-100" />
        ) : rows.length === 0 ? (
          <div className="rounded-3xl border border-slate-200 bg-white p-8 text-center">
            <FileTextIcon className="mx-auto h-7 w-7 text-slate-300" />
            <p className="mt-2 text-sm font-semibold text-slate-700">Nothing presented yet</p>
            <p className="mx-auto mt-1 max-w-[30ch] text-xs leading-relaxed text-slate-500">
              Repair options and replacement quotes you build on calls land here.
            </p>
          </div>
        ) : (
          rows.map((r) => {
            const st = quoteDisplayStatus({ status: r.status, viewed_at: r.viewed_at, expires_at: r.expires_at });
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => { if (r.token) window.open(`/proposal?t=${r.token}`, "_blank"); }}
                className="flex w-full items-center gap-3 rounded-3xl border border-slate-200 bg-white p-3.5 text-left shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)] [touch-action:manipulation] active:scale-[0.99]"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-[14.5px] font-bold text-slate-900">{r.customer}</span>
                    <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-[10px] font-extrabold uppercase", Q_CHIP[st] ?? Q_CHIP.draft)}>{st}</span>
                  </div>
                  <div className="mt-0.5 truncate text-[12px] text-slate-500">
                    {r.title ?? (r.quote_type === "service" ? "Repair options" : "Replacement quote")} · {fmtDay(r.sent_at ?? r.created_at)}
                  </div>
                </div>
                {r.total != null && r.total > 0 && (
                  <span className={cn("shrink-0 text-[15px] font-extrabold tabular-nums", st === "accepted" ? "text-emerald-600" : "text-slate-800")}>
                    {money0(r.total)}
                  </span>
                )}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
